/**
 * Create Scenario layout with additional steps sidebar
 */
import {
    Help as HelpIcon,
    Logout,
    Menu as MenuIcon,
    MenuOpen,
    Person,
} from '@mui/icons-material';
import {
    AppBar,
    Avatar,
    Box,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    ScenarioWorkflowMobileContext,
    ScenarioWorkflowMobileNav,
} from '@/contexts/ScenarioWorkflowMobileContext';
import { useAuth } from '@/hooks/useAuth';
import { useInstructionsGuideStore } from '@/store/instructionsGuideStore';
import { Sidebar, SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from './Sidebar';

interface CreateScenarioLayoutProps {
  children: React.ReactNode;
}

export const CreateScenarioLayout: React.FC<CreateScenarioLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const openGuide = useInstructionsGuideStore((s) => s.openGuide);
  const [scenarioMobileNav, setScenarioMobileNav] = useState<ScenarioWorkflowMobileNav | null>(null);
  const registerScenarioMobileNav = useCallback((nav: ScenarioWorkflowMobileNav | null) => {
    setScenarioMobileNav(nav);
  }, []);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/auth');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleLogoClick = () => {
    navigate('/app/home');
  };

  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    const first = firstName?.charAt(0).toUpperCase() || '';
    const last = lastName?.charAt(0).toUpperCase() || '';
    return first + last || user?.email?.charAt(0).toUpperCase() || '?';
  };

  return (
    <ScenarioWorkflowMobileContext.Provider value={{ register: registerScenarioMobileNav }}>
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {isMobile && scenarioMobileNav && (
            <Tooltip
              title={
                scenarioMobileNav.readOnly
                  ? `Section: ${scenarioMobileNav.title}`
                  : `Step: ${scenarioMobileNav.title}`
              }
            >
              <IconButton
                color="inherit"
                aria-label={scenarioMobileNav.readOnly ? 'Open scenario sections' : 'Open scenario steps'}
                onClick={scenarioMobileNav.onOpen}
                sx={{ mr: 1 }}
              >
                <MenuOpen />
              </IconButton>
            </Tooltip>
          )}

          {/* Logo/Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                cursor: 'pointer',
                color: 'primary.main',
                fontWeight: 'bold',
                '&:hover': {
                  color: 'primary.dark',
                }
              }}
              onClick={handleLogoClick}
            >
              QuickField
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  '& .help-icon': {
                    backgroundColor: 'primary.light',
                    color: 'primary.dark',
                  }
                }
              }}
              onClick={() => openGuide()}
            >
              <IconButton
                size="small"
                className="help-icon"
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'primary.dark',
                  }
                }}
                title="User Guide"
              >
                <HelpIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Help center
              </Typography>
            </Box>
          </Box>

          {/* User menu */}
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
                {user.full_name || user.email}
              </Typography>

              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenuOpen}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {getInitials(user.first_name, user.last_name)}
                </Avatar>
              </IconButton>

              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: { minWidth: 200 }
                }}
              >
                <MenuItem onClick={handleProfileClick}>
                  <ListItemIcon>
                    <Person fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Profile</ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Navigation Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { md: sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH },
          flexShrink: { md: 0 },
          transition: 'width 0.3s ease',
          mt: 8, // Account for AppBar height
        }}
      >
        {isMobile ? (
          <Sidebar
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
          />
        ) : (
          <Sidebar
            variant="permanent"
            collapsed={sidebarCollapsed}
            onToggleCollapse={handleSidebarToggle}
          />
        )}
      </Box>

      {/* Main content - NO LEFT MARGIN */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0, // No padding
          mt: 8, // Account for AppBar height
          height: 'calc(100vh - 64px)', // Full height minus AppBar
          overflow: 'hidden', // Disable scrolling for main content
          minWidth: 0, // Allow content to shrink
          boxSizing: 'border-box',
        }}
      >
        {React.isValidElement(children)
          ? React.cloneElement(children, { mainSidebarCollapsed: sidebarCollapsed })
          : children}
      </Box>
    </Box>
    </ScenarioWorkflowMobileContext.Provider>
  );
};
